import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";

import { useSelector, useDispatch } from "react-redux"; // Import useSelector and useDispatch
import { getInvocations, getLogData } from "@redux/slices/integrationSlice"; // Import the async thunk

import { formatDate } from "@utils/utils";

const Invocations = ({ details, onButtonClick }) => {
  const dispatch = useDispatch();

  const runHistory = useSelector((state) => state.integration.runHistory);
  const logData = useSelector((state) => state.integration.logData);
  const status = useSelector((state) => state.integration.status);
  const logDataLoading = useSelector((state) => state.integration.logdataLoadingStatus);



  const [selectedItem, setSelectedItem] = useState(null);

  // ----- NEW: Search state -----
  const [searchQuery, setSearchQuery] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // ----- NEW: Date filter state -----
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [datePreset, setDatePreset] = useState("");

  // ----- NEW: Log level filter state -----
  const [logLevelFilters, setLogLevelFilters] = useState({
    Info: true,
    Error: true,
    Warning: true,
  });

  // ----- NEW: Expanded log rows -----
  const [expandedRows, setExpandedRows] = useState(new Set());

  const logPanelRef = useRef(null);

  useEffect(() => {
    if (!details || !details.integration_id) {
      return;
    }
    dispatch(getInvocations(details.integration_id, 1));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]); // Empty dependency array means this runs once on mount

  useEffect(() => {
    if (!selectedItem) {
      return;
    }
    dispatch(getLogData(selectedItem.run_id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]); // Empty dependency array means this runs once on mount

  useEffect(() => {
    if (runHistory.length > 0) {
      setSelectedItem(runHistory[0]);
    }
  }, [runHistory]);

  // Reset search and expanded rows when log data changes
  useEffect(() => {
    setSearchQuery("");
    setCurrentMatchIndex(0);
    setExpandedRows(new Set());
  }, [logData]);

  const onItemSelected = (item) => {
    setSelectedItem(item);
  };

  // ----- NEW: Date filtering logic -----
  const setDatePresetHandler = useCallback((preset) => {
    setDatePreset(preset);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const toDateStr = now.toISOString().slice(0, 10);

    switch (preset) {
      case "today":
        setDateFrom(toDateStr);
        setDateTo(toDateStr);
        break;
      case "7days": {
        const from = new Date(today);
        from.setDate(from.getDate() - 7);
        setDateFrom(from.toISOString().slice(0, 10));
        setDateTo(toDateStr);
        break;
      }
      case "30days": {
        const from = new Date(today);
        from.setDate(from.getDate() - 30);
        setDateFrom(from.toISOString().slice(0, 10));
        setDateTo(toDateStr);
        break;
      }
      default:
        break;
    }
  }, []);

  const clearDateFilter = useCallback(() => {
    setDateFrom("");
    setDateTo("");
    setDatePreset("");
  }, []);

  // Filtered run history by date
  const filteredRunHistory = useMemo(() => {
    if (!runHistory) return [];
    if (!dateFrom && !dateTo) return runHistory;

    return runHistory.filter((item) => {
      const itemDate = new Date(item.startedAt);
      const itemDateStr = itemDate.toISOString().slice(0, 10);

      if (dateFrom && itemDateStr < dateFrom) return false;
      if (dateTo && itemDateStr > dateTo) return false;
      return true;
    });
  }, [runHistory, dateFrom, dateTo]);

  // Sync selectedItem with filteredRunHistory when date filter changes
  useEffect(() => {
    if (!filteredRunHistory || filteredRunHistory.length === 0) {
      setSelectedItem(null);
    } else if (!selectedItem || !filteredRunHistory.includes(selectedItem)) {
      setSelectedItem(filteredRunHistory[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRunHistory]);

  // ----- Log filtering: level filter + search -----
  const filteredLogData = useMemo(() => {
    if (!logData) return [];

    // Step 1: Filter by log level
    let filtered = logData.filter((item) => {
      const level = item.logType || "Info";
      return logLevelFilters[level] !== false;
    });

    // Check if a target string partially matches the query 
    // Uses direct inclusion and 50% word matching fallback
    const isPartialMatch = (queryStr, target) => {
      if (!target) return false;
      const t = target.toLowerCase();
      
      // 1. Direct inclusion
      if (t.includes(queryStr)) return true;
      
      // 2. Word by word match (at least 50% words must match)
      const words = queryStr.split(/\s+/).filter(Boolean);
      if (words.length > 0) {
        const matchedWords = words.filter(word => t.includes(word));
        if (matchedWords.length / words.length >= 0.5) return true;
      }
      
      // 3. Sequential character match (50% character match)
      let qIndex = 0;
      let matchCount = 0;
      for (let i = 0; i < t.length; i++) {
        if (qIndex < queryStr.length && t[i] === queryStr[qIndex]) {
          matchCount++;
          qIndex++;
        }
      }
      
      return (queryStr.length > 0 && matchCount / queryStr.length >= 0.5);
    };

    // Step 2: Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter((item) => {
        const message = item.logMessage || "";
        return isPartialMatch(query, message);
      });
    }

    return filtered;
  }, [logData, logLevelFilters, searchQuery]);

  // Search matches (within already-filtered data, for highlighting & navigation)
  const searchMatches = useMemo(() => {
    if (!searchQuery.trim() || !filteredLogData.length) return [];

    const isPartialMatch = (queryStr, target) => {
      if (!target) return false;
      const t = target.toLowerCase();
      if (t.includes(queryStr)) return true;
      
      const words = queryStr.split(/\s+/).filter(Boolean);
      if (words.length > 0) {
        const matchedWords = words.filter(word => t.includes(word));
        if (matchedWords.length / words.length >= 0.5) return true;
      }
      
      let qIndex = 0;
      let matchCount = 0;
      for (let i = 0; i < t.length; i++) {
        if (qIndex < queryStr.length && t[i] === queryStr[qIndex]) {
          matchCount++;
          qIndex++;
        }
      }
      return (queryStr.length > 0 && matchCount / queryStr.length >= 0.5);
    };

    const query = searchQuery.toLowerCase();
    const matches = [];

    filteredLogData.forEach((item, index) => {
      const message = item.logMessage || "";

      if (isPartialMatch(query, message)) {
        matches.push(index);
      }
    });

    return matches;
  }, [searchQuery, filteredLogData]);

  // Reset current match index when search changes
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchQuery]);

  const navigateMatch = useCallback(
    (direction) => {
      if (searchMatches.length === 0) return;

      let newIndex;
      if (direction === "next") {
        newIndex = (currentMatchIndex + 1) % searchMatches.length;
      } else {
        newIndex =
          (currentMatchIndex - 1 + searchMatches.length) %
          searchMatches.length;
      }
      setCurrentMatchIndex(newIndex);

      // Scroll to the match
      const matchElement = document.getElementById(
        `log-row-${searchMatches[newIndex]}`
      );
      if (matchElement) {
        matchElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    [currentMatchIndex, searchMatches]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );

  const toggleLogLevel = useCallback((level) => {
    setLogLevelFilters((prev) => ({
      ...prev,
      [level]: !prev[level],
    }));
  }, []);

  const toggleRowExpansion = useCallback((index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // We removed highlighting so it just returns the normal text 
  // without span wrapping the fuzzy search results.
  const highlightText = useCallback(
    (text, isCurrentMatch) => {
      return text;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const StatusIcon = ({ status }) => {
    switch (status) {
      case "Success":
        return (
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              {" "}
              <path
                d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z"
                fill="#15e411"
              ></path>{" "}
            </g>
          </svg>
        );
      case "Failed":
        return (
          <svg
            className="h-4 w-4"
            viewBox="0 0 32 32"
            version="1.1"
            fill="#000000"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              {" "}
              <defs> </defs>{" "}
              <g
                id="Page-1"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
              >
                {" "}
                <g
                  id="Icon-Set-Filled"
                  transform="translate(-570.000000, -1089.000000)"
                  fill="#f24545"
                >
                  {" "}
                  <path
                    d="M591.657,1109.24 C592.048,1109.63 592.048,1110.27 591.657,1110.66 C591.267,1111.05 590.633,1111.05 590.242,1110.66 L586.006,1106.42 L581.74,1110.69 C581.346,1111.08 580.708,1111.08 580.314,1110.69 C579.921,1110.29 579.921,1109.65 580.314,1109.26 L584.58,1104.99 L580.344,1100.76 C579.953,1100.37 579.953,1099.73 580.344,1099.34 C580.733,1098.95 581.367,1098.95 581.758,1099.34 L585.994,1103.58 L590.292,1099.28 C590.686,1098.89 591.323,1098.89 591.717,1099.28 C592.11,1099.68 592.11,1100.31 591.717,1100.71 L587.42,1105.01 L591.657,1109.24 L591.657,1109.24 Z M586,1089 C577.163,1089 570,1096.16 570,1105 C570,1113.84 577.163,1121 586,1121 C594.837,1121 602,1113.84 602,1105 C602,1096.16 594.837,1089 586,1089 L586,1089 Z"
                    id="cross-circle"
                  >
                    {" "}
                  </path>{" "}
                </g>{" "}
              </g>{" "}
            </g>
          </svg>
        );
      case "Running":
        return (
          <svg
            className="h-4 w-4"
            viewBox="0 0 32 32"
            version="1.1"
            fill="#000000"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g
              id="SVGRepo_tracerCarrier"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></g>
            <g id="SVGRepo_iconCarrier">
              {" "}
              <defs> </defs>{" "}
              <g
                id="Page-1"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
              >
                {" "}
                <g
                  id="Icon-Set-Filled"
                  transform="translate(-570.000000, -1089.000000)"
                  fill="#0000ff"
                >
                  {" "}
                  <path
                    d="M591.657,1109.24 C592.048,1109.63 592.048,1110.27 591.657,1110.66 C591.267,1111.05 590.633,1111.05 590.242,1110.66 L586.006,1106.42 L581.74,1110.69 C581.346,1111.08 580.708,1111.08 580.314,1110.69 C579.921,1110.29 579.921,1109.65 580.314,1109.26 L584.58,1104.99 L580.344,1100.76 C579.953,1100.37 579.953,1099.73 580.344,1099.34 C580.733,1098.95 581.367,1098.95 581.758,1099.34 L585.994,1103.58 L590.292,1099.28 C590.686,1098.89 591.323,1098.89 591.717,1099.28 C592.11,1099.68 592.11,1100.31 591.717,1100.71 L587.42,1105.01 L591.657,1109.24 L591.657,1109.24 Z M586,1089 C577.163,1089 570,1096.16 570,1105 C570,1113.84 577.163,1121 586,1121 C594.837,1121 602,1113.84 602,1105 C602,1096.16 594.837,1089 586,1089 L586,1089 Z"
                    id="cross-circle"
                  >
                    {" "}
                  </path>{" "}
                </g>{" "}
              </g>{" "}
            </g>
          </svg>
        );
      default:
        return <span>&nbsp;</span>;
    }
  };

  const onRefresh = () => {
    dispatch(getInvocations(details.integration_id, 1));
  };

  const LoadingSkeleton = () => {
    return (
      <div className="space-y-2">
        {/* Generate 20 skeleton items */}
        {Array.from({ length: 20 }).map((_, index) => (
          <div
            key={index}
            className="flex animate-pulse space-x-4 border-b border-gray-700"
          >
            <div className="h-8 w-full bg-[#EEEEEE] py-2"></div>
          </div>
        ))}
      </div>
    );
  };

  // Check if a log message is long (for expand/collapse)
  const isLongMessage = (message) => {
    return message && message.length > 200;
  };

  // Count log levels for the current data
  const logLevelCounts = useMemo(() => {
    if (!selectedItem || !logData) return { Info: 0, Error: 0, Warning: 0 };
    const counts = { Info: 0, Error: 0, Warning: 0 };
    logData.forEach((item) => {
      const level = item.logType || "Info";
      if (counts[level] !== undefined) {
        counts[level]++;
      }
    });
    return counts;
  }, [logData, selectedItem]);

  return (
    <div className="mx-auto flex h-full min-h-0 max-w-full flex-col overflow-hidden rounded-2xl border-2 border-black bg-white pt-2 shadow-sm">
      {/* ===== HEADER ===== */}
      <div className="px-4 pb-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between text-gray-900 border-b-2 border-black pb-2 gap-4">
          <div className="flex items-center justify-between md:justify-start md:space-x-6 w-full md:w-auto">
            <div className="text-sm md:text-base font-black tracking-tight text-blue-600 font-sans">Log Flare</div>
            <button
              className="group flex items-center space-x-1 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-2.5 md:px-3 py-1 text-[8px] md:text-[9px] font-black text-[#1e3a8a] transition-all duration-300 hover:bg-[#e0efff] hover:border-[#b8d9ff] hover:shadow-md active:scale-95 tracking-wider whitespace-nowrap"
              onClick={onRefresh}
            >
              <svg className="h-3 w-3 transition-transform duration-500 group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-center w-full md:w-auto gap-3">
            <div className="relative w-full sm:w-64 lg:w-80">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-full border border-blue-400 bg-gray-50 py-2.5 pl-11 pr-10 text-xs text-gray-900 placeholder-gray-500 outline-none transition-all duration-300 focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10 shadow-inner"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-900"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {searchQuery && selectedItem && (
              <div className="flex items-center space-x-1.5">
                <span className="whitespace-nowrap text-xs text-gray-500">
                  {searchMatches.length > 0 ? `${currentMatchIndex + 1} of ${searchMatches.length}` : "No matches"}
                </span>
                <button onClick={() => navigateMatch("prev")} disabled={searchMatches.length === 0} className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white disabled:opacity-30">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                </button>
                <button onClick={() => navigateMatch("next")} disabled={searchMatches.length === 0} className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white disabled:opacity-30">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col space-y-4 px-4 pb-4 text-gray-900 lg:flex-row lg:space-x-4 lg:space-y-0">
        {/* ===== LEFT PANEL: Run History with Date Filter ===== */}
        <div className="flex w-full min-h-0 flex-col lg:w-1/5">
          {/* Date Filter Section */}
          <div className="mb-1.5 rounded-2xl border-2 border-black bg-gray-50/50 p-1.5 shadow-sm">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-black text-[#334155]">
                Filter by Date
              </span>
              {(dateFrom || dateTo) && (
                <button
                  onClick={clearDateFilter}
                  className="rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[9px] font-bold text-red-400 transition-all hover:bg-red-500/20"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Quick presets */}
            <div className="mb-2 flex gap-1.5">
              {[
                { key: "today", label: "Today" },
                { key: "7days", label: "7D" },
                { key: "30days", label: "30D" },
              ].map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => setDatePresetHandler(preset.key)}
                  className={`flex-1 rounded-full px-2.5 py-0.5 text-[9px] font-black transition-all duration-300 ${datePreset === preset.key
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30 active:scale-95"
                    : "bg-white text-gray-600 border border-blue-400 hover:bg-gray-100"
                    }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Date inputs */}
            <div className="space-y-1">
              <div>
                <label className="mb-0.5 block text-[8px] text-gray-500">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setDatePreset("");
                  }}
                  className="w-full rounded-md border border-gray-400 bg-[#EEEEEE] px-1.5 py-0.5 text-[10px] text-gray-900 outline-none transition-colors focus:border-[#03bfff]/60 focus:ring-1 focus:ring-[#03bfff]/20"
                />
              </div>
              <div>
                <label className="mb-0.5 block text-[8px] text-gray-500">To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setDatePreset("");
                  }}
                  className="w-full rounded-md border border-gray-400 bg-[#EEEEEE] px-1.5 py-0.5 text-[10px] text-gray-900 outline-none transition-colors focus:border-[#03bfff]/60 focus:ring-1 focus:ring-[#03bfff]/20"
                />
              </div>
            </div>

          </div>

          {/* Log Level Filter Section */}
          <div className="mb-1.5 rounded-2xl border-2 border-black bg-gray-50/50 p-1.5 shadow-sm">
            <div className="mb-1">
              <span className="text-[10px] font-black text-blue-400">
                Log Level
              </span>
            </div>
            <div className="flex gap-1">
              {["Info", "Error", "Warning"].map((level) => {
                const colors = {
                  Info: {
                    active: "bg-blue-600 text-white shadow-lg shadow-blue-500/20",
                    inactive: "bg-white text-gray-600 border border-blue-400 hover:bg-gray-100",
                  },
                  Error: {
                    active: "bg-red-600 text-white shadow-lg shadow-red-500/20",
                    inactive: "bg-white text-gray-600 border border-blue-400 hover:bg-gray-100",
                  },
                  Warning: {
                    active: "bg-yellow-500 text-black shadow-lg shadow-yellow-500/20",
                    inactive: "bg-white text-gray-600 border border-blue-400 hover:bg-gray-100",
                  },
                };
                return (
                  <button
                    key={level}
                    onClick={() => toggleLogLevel(level)}
                    className={`flex-1 flex flex-row items-center justify-center gap-x-1 rounded-2xl py-1 px-1 text-[9px] font-black transition-all duration-300 active:scale-95 border ${logLevelFilters[level]
                      ? colors[level].active
                      : colors[level].inactive
                      }`}
                  >
                    <span>{level}</span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${logLevelFilters[level] 
                      ? "bg-white text-gray-900"
                      : "bg-gray-100 text-gray-500"}`}>
                      {logLevelCounts[level] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Run History List */}
          <div className="h-[calc(100vh-485px)] min-h-[220px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-600">
            {status === "succeeded" && runHistory && runHistory.length === 0 && (
              <div className="rounded-md bg-theme-tertiary p-3 text-center text-sm text-gray-400">
                No data available
              </div>
            )}

            {status === "loading" && <LoadingSkeleton />}
            {status === "succeeded" &&
              filteredRunHistory &&
              filteredRunHistory.map((item, index) => (
                <div
                  onClick={() => onItemSelected(item)}
                  className={`group mt-1 flex cursor-pointer items-center rounded-xl border-b border-gray-100 px-2 transition-all duration-150 ${selectedItem === item
                    ? "bg-blue-50 text-blue-700 border-l-4 border-l-blue-600 shadow-sm"
                    : "hover:bg-gray-50"
                    }`}
                  key={index}
                >
                  <div className="flex w-2/3 items-center space-x-1.5 py-2.5">
                    <span className="flex-shrink-0">
                      <StatusIcon status={item.status} />
                    </span>
                    <span className="text-[14px] leading-tight text-[#424242] font-semibold">
                      {formatDate(item.startedAt)}
                    </span>
                  </div>

                </div>
              ))}
          </div>
        </div>

        {/* ===== RIGHT PANEL: Log Viewer ===== */}
        <div className="flex w-full min-h-0 flex-col lg:w-4/5">
          {/* Log Content Panel */}
          <div
            ref={logPanelRef}
            className="inner-strong-shadow h-[calc(100vh-250px)] min-h-[480px] overflow-y-auto rounded-2xl bg-white text-gray-900 border-2 border-black"
            style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
          >
            {!selectedItem && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <svg className="mb-3 h-10 w-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <span className="text-sm">No logs available</span>
              </div>
            )}

            {selectedItem && logDataLoading === "loading" && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3 text-gray-400">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-sm">Loading logs...</span>
                </div>
              </div>
            )}

            {selectedItem && logDataLoading === "succeeded" && filteredLogData.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <svg className="mb-3 h-10 w-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <span className="text-sm">No log data available</span>
              </div>
            )}


            {selectedItem && logDataLoading === "succeeded" &&
              filteredLogData.map((item, index) => {
                const isCurrentHighlight =
                  searchMatches[currentMatchIndex] === index;
                const isExpanded = expandedRows.has(index);
                const isLong = isLongMessage(item.logMessage);
                const displayMessage =
                  !isExpanded && isLong
                    ? item.logMessage.slice(0, 200) + "..."
                    : item.logMessage;

                const logEntryDate = (() => {
                  if (!item.loggedAt) return "";
                  return formatDate(item.loggedAt);
                })();

                return (
                  <div
                    id={`log-row-${index}`}
                    key={index}
                    className="group flex items-start border-b border-gray-300 px-4 py-3.5 transition-colors duration-100 bg-white hover:bg-gray-50/80"
                  >
                    {/* Timestamp */}
                    <span className="mr-6 flex-shrink-0 whitespace-nowrap text-[14px] text-black font-normal">
                      {logEntryDate}
                    </span>

                    {/* Log Message */}
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-[14px] leading-relaxed text-black font-normal`}
                        style={{
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap"
                        }}
                      >
                        {highlightText(displayMessage, isCurrentHighlight)}
                      </div>
                      {isLong && (
                        <button
                          onClick={() => toggleRowExpansion(index)}
                          className="mt-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium text-[#03bfff] transition-colors hover:bg-[#03bfff]/10"
                        >
                          {isExpanded ? "▲ Collapse" : "▼ Expand"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

            {/* Log Summary Footer */}
            {selectedItem && logDataLoading === "succeeded" && filteredLogData.length > 0 && (
              <div className="sticky bottom-0 left-0 right-0 z-10 w-full border-t border-gray-200 bg-white px-4 py-2">
                <div className="flex flex-wrap items-center justify-between gap-3 text-[14px] text-gray-600 font-bold">
                  <span>{filteredLogData.length} log entries</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center space-x-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[12px] text-blue-700">
                      <span className="inline-block h-2 w-2 rounded-full bg-[#03bfff]"></span>
                      <span>Info: {logLevelCounts.Info}</span>
                    </span>
                    <span className="flex items-center space-x-1 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[12px] text-red-700">
                      <span className="inline-block h-2 w-2 rounded-full bg-red-400"></span>
                      <span>Error: {logLevelCounts.Error}</span>
                    </span>
                    <span className="flex items-center space-x-1 rounded-full border border-yellow-200 bg-yellow-50 px-3 py-1 text-[12px] text-yellow-700">
                      <span className="inline-block h-2 w-2 rounded-full bg-yellow-400"></span>
                      <span>Warning: {logLevelCounts.Warning}</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invocations;
